import dayjs from 'dayjs';
import type {
  CashflowForecastApiAccount,
  CashflowForecastApiResponse,
  CashflowForecastMockDefinition,
  CashflowForecastDashboardResponse,
  CashflowPredictionSnapshot,
  ForecastScenarioDefinition,
  ForecastScenarioKey,
  ForecastScenarioMetrics,
} from './types';

export const CASHFLOW_FORECAST_DASHBOARD_MOCK: CashflowForecastMockDefinition =
  {
    title: '现金流预测 · CEO 主看板',
    subtitle: 'v3 · 首期 MVP · 默认进入缺省态',
    dataUpdatedAt: '——',
    quickRangeDays: [30, 90, 180],
    riskConfig: {
      floorMonths: 0,
      breakpoints: [12, 6, 4.5, 3, 0],
      segments: [
        {
          key: 'safe',
          label: '安全',
          description: '安全 (≥6月)',
          color: '#10b981',
          textColor: '#059669',
          lowerBoundMonths: 6,
        },
        {
          key: 'watch',
          label: '关注',
          description: '关注 (4.5~6月)',
          color: '#f59e0b',
          textColor: '#d97706',
          lowerBoundMonths: 4.5,
        },
        {
          key: 'risk',
          label: '风险',
          description: '风险 (3~4.5月)',
          color: '#f97316',
          textColor: '#ea580c',
          lowerBoundMonths: 3,
        },
        {
          key: 'critical',
          label: '紧急',
          description: '紧急 (<3月)',
          color: '#991b1b',
          textColor: '#b91c1c',
          lowerBoundMonths: 0,
        },
      ],
    },
    scenarioDefinitions: [
      { key: 'base', label: '中性', color: '#111827' },
      { key: 'opt', label: '乐观', color: '#2563eb' },
      { key: 'pess', label: '悲观', color: '#f97316' },
    ],
    branchBalances: [
      {
        id: 'sh',
        name: '上海',
        amountWan: 0,
        updatedAt: '',
      },
      {
        id: 'bj',
        name: '北京',
        amountWan: 0,
        updatedAt: '',
      },
      {
        id: 'sz',
        name: '深圳',
        amountWan: 0,
        updatedAt: '',
      },
      {
        id: 'sd',
        name: '山东',
        amountWan: 0,
        updatedAt: '',
      },
    ],
  };

const SCENARIO_KEY_TO_LABEL_MAP: Record<ForecastScenarioKey, '中性' | '乐观' | '悲观'> = {
  base: '中性',
  opt: '乐观',
  pess: '悲观',
};

const SCENARIO_METRIC_LABELS = {
  inflow: '含合同回款 + 商机加权 + 借款/融资',
  outflow: '含刚性支出 + 应付采购费 + 税费 + 银行本息还款等',
  netCashflow: '预测期累计',
  endingBalance: '预测窗口截止日',
} as const;

const DEFAULT_SCENARIO_METRICS: ForecastScenarioMetrics = {
  inflow: {
    valueWan: 0,
    description: SCENARIO_METRIC_LABELS.inflow,
    tone: 'positive',
  },
  outflow: {
    valueWan: 0,
    description: SCENARIO_METRIC_LABELS.outflow,
    tone: 'negative',
  },
  netCashflow: {
    valueWan: 0,
    description: SCENARIO_METRIC_LABELS.netCashflow,
    tone: 'neutral',
  },
  endingBalance: {
    valueWan: 0,
    description: SCENARIO_METRIC_LABELS.endingBalance,
    tone: 'neutral',
  },
};

const cloneDefaultScenarioMetrics = (): ForecastScenarioMetrics => ({
  inflow: { ...DEFAULT_SCENARIO_METRICS.inflow },
  outflow: { ...DEFAULT_SCENARIO_METRICS.outflow },
  netCashflow: { ...DEFAULT_SCENARIO_METRICS.netCashflow },
  endingBalance: { ...DEFAULT_SCENARIO_METRICS.endingBalance },
});

const toNumber = (value?: string): number => {
  if (!value) return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const yuanToWan = (value?: string): number => {
  return Number((toNumber(value) / 10000).toFixed(4));
};

const buildBranchId = (name: string, index: number): string => {
  const normalized = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\u4e00-\u9fa5]+/g, '-');

  return normalized || `branch-${index + 1}`;
};

const buildBranchBalance = (
  account: CashflowForecastApiAccount,
  index: number
) => {
  const accountName = account.账户名称?.trim() || `账户${index + 1}`;

  return {
    id: buildBranchId(accountName, index),
    name: accountName,
    amountWan: yuanToWan(account.账户余额),
    updatedAt: account.更新日期 || '',
  };
};

export function createEmptyPredictionSnapshot(
  endDate: string,
  scenarioDefinitions: ForecastScenarioDefinition[] =
    CASHFLOW_FORECAST_DASHBOARD_MOCK.scenarioDefinitions,
  branchBalances = CASHFLOW_FORECAST_DASHBOARD_MOCK.branchBalances
): CashflowPredictionSnapshot {
  const startDate = dayjs().startOf('day');
  const normalizedEndDate = dayjs(endDate).isValid()
    ? dayjs(endDate).startOf('day')
    : startDate;
  const scenarioMetrics = scenarioDefinitions.reduce(
    (result, scenario) => {
      result[scenario.key] = cloneDefaultScenarioMetrics();
      return result;
    },
    {} as CashflowPredictionSnapshot['scenarioMetrics']
  );

  return {
    generatedAt: dayjs().format('YYYY-MM-DD HH:mm'),
    rangeStart: startDate.format('YYYY-MM-DD'),
    rangeEnd: normalizedEndDate.format('YYYY-MM-DD'),
    currentBalanceWan: 0,
    currentBalanceUpdatedAt: '',
    branchBalances,
    riskLevelLabel: '',
    riskAvailableDays: 0,
    riskCoverageMonths: 0,
    riskReferenceDate: '',
    scenarioMetrics,
  };
}

export function createCashflowForecastDashboardResponse(
  apiResponse: CashflowForecastApiResponse,
  predictionEndDate: string,
  currentDefinition: CashflowForecastMockDefinition =
    CASHFLOW_FORECAST_DASHBOARD_MOCK
): CashflowForecastDashboardResponse {
  const now = dayjs();
  const normalizedRangeEnd = dayjs(predictionEndDate).isValid()
    ? dayjs(predictionEndDate).format('YYYY-MM-DD')
    : now.format('YYYY-MM-DD');
  const totalAccount = apiResponse.总公司账户;
  const branchBalances = apiResponse.子公司账户列表?.length
    ? apiResponse.子公司账户列表.map(buildBranchBalance)
    : currentDefinition.branchBalances;
  const scenarioMetrics = currentDefinition.scenarioDefinitions.reduce(
    (result, scenario) => {
      const rawMetrics = apiResponse.情景预测?.[SCENARIO_KEY_TO_LABEL_MAP[scenario.key]];
      const inflow = yuanToWan(rawMetrics?.总流入);
      const outflow = yuanToWan(rawMetrics?.总流出);
      const netCashflow = yuanToWan(rawMetrics?.净现金流);
      const endingBalance = yuanToWan(rawMetrics?.期末现金余额);

      result[scenario.key] = {
        inflow: {
          valueWan: inflow,
          description: SCENARIO_METRIC_LABELS.inflow,
          tone: 'positive',
        },
        outflow: {
          valueWan: outflow,
          description: SCENARIO_METRIC_LABELS.outflow,
          tone: 'negative',
        },
        netCashflow: {
          valueWan: netCashflow,
          description: SCENARIO_METRIC_LABELS.netCashflow,
          tone: netCashflow > 0 ? 'positive' : netCashflow < 0 ? 'negative' : 'neutral',
        },
        endingBalance: {
          valueWan: endingBalance,
          description: SCENARIO_METRIC_LABELS.endingBalance,
          tone: 'neutral',
        },
      };

      return result;
    },
    {} as CashflowPredictionSnapshot['scenarioMetrics']
  );
  const riskPrediction = apiResponse.风险预测;
  const generatedAt = now.format('YYYY-MM-DD HH:mm');

  return {
    ...currentDefinition,
    generatedAt,
    dataUpdatedAt: generatedAt,
    rangeStart: now.format('YYYY-MM-DD'),
    rangeEnd: normalizedRangeEnd,
    currentBalanceWan: yuanToWan(totalAccount?.账户余额),
    currentBalanceUpdatedAt: totalAccount?.更新日期 || now.format('YYYY-MM-DD'),
    branchBalances,
    riskLevelLabel: riskPrediction?.风险等级 || '',
    riskAvailableDays: toNumber(riskPrediction?.可持续天数),
    riskCoverageMonths: toNumber(riskPrediction?.覆盖月数),
    riskReferenceDate: riskPrediction?.覆盖日期 || now.format('YYYY-MM-DD'),
    scenarioMetrics,
  };
}
